import { queryClient } from "./queryClient"
import { isPrerequisiteFailedError } from "./PrerequisiteFailedError"
import { isPartialSuccessError } from "./PartialSuccessError"
import { ENTITY_CREATE_MUTATION_KEYS } from "@/lib/queryClient/mutationKeys"

/**
 * Check if a mutation key matches one of the gating keys.
 */
function isGatingMutationKey(key: readonly unknown[] | undefined): boolean {
  if (!key) return false
  return ENTITY_CREATE_MUTATION_KEYS.some(
    (gatingKey) =>
      gatingKey.length === key.length &&
      gatingKey.every((part, i) => part === key[i]),
  )
}

/**
 * Subscribe to mutation cache events for two purposes:
 *
 * 1. **Cascade retry**: When a gating CREATE mutation succeeds, find all
 *    PrerequisiteFailedError mutations in the same scope and re-execute them.
 *
 * 2. **PartialSuccessError cleanup**: When a mutation enters error state with
 *    PartialSuccessError, remove it from the cache. The onError handler has
 *    already bridged the entity/photos and spawned retry mutations, so the
 *    combined mutation is no longer needed. This acts as a reliable backup
 *    for the queueMicrotask cleanup in the onError handlers.
 */
export function setupScopeCascadeRetry() {
  return queryClient.getMutationCache().subscribe((event) => {
    if (event.type !== "updated") return
    const mutation = event.mutation

    // --- PartialSuccessError cleanup ---
    // When a combined CREATE mutation fails with PartialSuccessError, the
    // onError handler bridges the entity and spawns individual photo retries.
    // Remove the combined mutation since it's fully handled.
    if (
      mutation.state.status === "error" &&
      isPartialSuccessError(mutation.state.error)
    ) {
      // Use setTimeout to let TanStack Query finish its internal state updates
      setTimeout(() => {
        queryClient.getMutationCache().remove(mutation)
      }, 0)
      return
    }

    // --- Cascade retry ---
    // Only react to successful completions of gating mutations
    if (mutation.state.status !== "success") return
    if (!isGatingMutationKey(mutation.options.mutationKey)) return

    const scopeId = mutation.options.scope?.id
    if (!scopeId) return

    // Find all failed mutations in the same scope with PrerequisiteFailedError
    const blockedMutations = queryClient
      .getMutationCache()
      .getAll()
      .filter((m) => {
        if (m === mutation) return false
        if (m.options.scope?.id !== scopeId) return false
        if (m.state.status !== "error") return false
        return isPrerequisiteFailedError(m.state.error)
      })

    // Re-execute each blocked mutation
    for (const blocked of blockedMutations) {
      const variables = blocked.state.variables

      // Remove the failed mutation from cache
      queryClient.getMutationCache().remove(blocked)

      // Rebuild with defaults (restores mutationFn/onSuccess after rehydration)
      const rebuilt = queryClient.getMutationCache().build(queryClient, {
        ...queryClient.defaultMutationOptions(blocked.options),
        ...blocked.options,
      })

      // Execute with original variables
      rebuilt.execute(variables)
    }
  })
}
